import { Component, DestroyRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { UserService } from '@core/api/user.service';
import { UtilitiesService } from '@core/api/utlities.service';
import * as fromApp from '@store';
import { selectCurrentUser } from '@store/auth/auth.selectors';
import { SpinnerService } from '@shared/spinner/spinner.service';
import { ColumnDef, DisplayTableFilterComponent } from '@shared/components/display-table-filter/display-table-filter.component';
import { UserActivity } from '@shared/models/user-activity.model';
import { DisplayTableService } from '@core/helper/display-table.service';

@Component({
  selector: 'fc-user-activity-dashboard',
  templateUrl: './user-activity-dashboard.component.html',
  styleUrls: ['./user-activity-dashboard.component.scss'],
  standalone: false
})
export class UserActivityDashboardComponent implements OnInit, OnDestroy {
  @ViewChild('filterComponent') child!: DisplayTableFilterComponent;
  readonly PAGE_NAME: string = 'user-activity';
  destroyRef = inject(DestroyRef);

  eventTypeFilter = [
    'Email Sent',
    'ERT Adjustment',
    'ID Verification',
    'Log In Success',
    'Log In Failure',
    'Password Changed',
    'Payment Adjustment',
    'Payment Made',
    'Payment Received',
    'Profile Updated',
    'Requesting Password Reset',
    'Site Modification',
  ];
  displayedColumns: string[] = ['userName', 'event', 'eventDetails', 'eventTime'];
  columnData: ColumnDef[] = [
    {
      field: 'user.fullname',
      type: 'text',
      operators: ['contains'],
      templateColumn: 'userName',
      label: 'User Name',
      rowValue: (row: UserActivity) => row.user?.getDisplayName(),
      // routerLink: (row: Site) => ['/admin', 'profile', row.applicantId],
      columnClass: 'column-large'
    },
    {
      field: 'event',
      type: 'select',
      templateColumn: 'event',
      operators: ['eq', 'not'],
      options: this.eventTypeFilter,
      label: 'Event Name',
      rowValue: (row: UserActivity) => row.event,
      columnClass: 'column-medium'
    },
    {
      templateColumn: 'eventDetails',
      label: 'Detail',
      rowValue: (row: UserActivity) => row.eventDetails,
      columnClass: 'column-fill'
    },
    {
      field: 'event_time',
      type: 'date',
      operators: ['lt', 'gt', 'between'],
      templateColumn: 'eventTime',
      label: 'Event Time',
      rowValue: (row: UserActivity) => row.eventTime,
      rowValuePipe: { name: 'date', args: ['short'] },
      columnClass: 'column-large'
    },
  ];


  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly route: ActivatedRoute,
    readonly router: Router,
    readonly userService: UserService,
    readonly spinnerService: SpinnerService,
    readonly displayTableService: DisplayTableService,
    readonly utilitiesService: UtilitiesService
  ) {
    this.displayTableService.setDisplayedColumns(this.displayedColumns);
    this.displayTableService.columnData = this.columnData.sort((a, b) => a.label.localeCompare(b.label));
    this.displayTableService.pageName = this.PAGE_NAME;
    this.displayTableService.queryItemServiceCall = (params) => this.userService.getUserActivity(params);
    this.displayTableService.setSortBy([['event_time', this.displayTableService.sortStates.descending]])
    this.displayTableService.setSortQueryByCurrentActiveSort();
  }

  ngOnInit() {
    this.store
      .select(selectCurrentUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(currentUser => {
        if (currentUser) {
          if (currentUser.isLandowner) {
            this.displayedColumns.splice(0, 1);
            this.displayTableService.setDisplayedColumns(this.displayedColumns);
            this.displayTableService.columnData = this.columnData.filter(col => col.templateColumn !== 'userName');
            this.displayTableService.queryItems();
          } else if (this.route.snapshot.queryParamMap.get('userId')) {
            // Add userId to columnDef table
            this.columnData.push(
              {
                field: 'user_id',
                type: 'number',
                operators: ['eq', 'lt', 'gt', 'between'],
                templateColumn: 'userId',
                label: 'User Id',
                rowValue: (row: UserActivity) => row.userId,
                columnClass: 'column-x-small',
              })
            this.displayTableService.columnData = this.columnData;
            // create userID filter
            let userId = this.route.snapshot.queryParamMap.get('userId');
            this.displayTableService.setActiveFilters([{
              "id": crypto.randomUUID(),
              "field": "user_id",
              "fieldLabel": "User ID",
              "operator": "eq",
              "value": userId,
              "valueTo": "null",
              "displayLabel": `User ID equals ${userId}`,
              "query": `user_id(eq,${userId})`
            }]);
          } else {
            this.displayTableService.queryItems();
          }
        }
      });
  }

  ngOnDestroy() {
    this.displayTableService.clearFilters(false);
    this.displayTableService.dataSource = null;
  }

}
